import { Action, createAction, handleActions } from 'redux-actions';
import axios from 'axios';
import { FULFILLED, PENDING, REJECTED } from './action-type.util';
import {
    getAxiosData,
    getRejectedRespData,
    initPagingList,
    IPaging,
    parsePagingToParams,
    parseToPaging,
    SERVER_API_URL,
} from '../config/constants';
import { IOkkoPay as IModel } from './okkoPay.model';
import { parseEmptyField } from '../shared/utils/utils';

export const ACTION_TYPES = {
    UPDATE_STATE: 'okkoPay/UPDATE_STATE',
    SET_PAGING: 'okkoPay/SET_PAGING',
    RESET_PAGING: 'okkoPay/RESET_PAGING',
    GET: 'okkoPay/GET',
    GET_LIST: 'okkoPay/GET_LIST',
    GET_BY_ID: 'okkoPay/GET_BY_ID',
    DELETE: 'okkoPay/DELETE',
    SAVE: 'okkoPay/SAVE',
};

export interface IOkkoPayState {
    loading: boolean;
    error: any;
    list: IModel[];
    listShort: IModel[];
    data: IModel;
    paging: IPaging;
}

const initState = {
    loading: false,
    error: null,
    list: [],
    listShort: [],
    data: {} as IModel,
    paging: { ...initPagingList },
};

export const updateState = createAction(ACTION_TYPES.UPDATE_STATE);
export const setPaging = createAction(ACTION_TYPES.SET_PAGING);
export const resetPaging = createAction(ACTION_TYPES.RESET_PAGING);

export const get = createAction<any, any>(ACTION_TYPES.GET, (params?: any) =>
    getAxiosData(
        axios.get(`${SERVER_API_URL}/okko-pay-services`, {
            params: parsePagingToParams({ ...initPagingList, ...params }),
        }),
    ),
);
export const getList = createAction(ACTION_TYPES.GET_LIST, (params: any) =>
    getAxiosData(axios.get(`${SERVER_API_URL}/okko-pay-services/list`, { params })),
);
export const getById = createAction(ACTION_TYPES.GET_BY_ID, (id: number) =>
    getAxiosData(axios.get(`${SERVER_API_URL}/okko-pay-services/${id}`)),
);
export const remove = createAction(ACTION_TYPES.DELETE, async (id: number) => {
    await getAxiosData(axios.delete(`${SERVER_API_URL}/okko-pay-services/${id}`));
    return id;
});
export const save = createAction(ACTION_TYPES.SAVE, (body: IModel) =>
    body.id
        ? getAxiosData(axios.put(`${SERVER_API_URL}/okko-pay-services/${body.id}`, parseEmptyField(body)))
        : getAxiosData(axios.post(`${SERVER_API_URL}/okko-pay-services`, parseEmptyField(body))),
);

/* Reducer function */
const onPending = (state: IOkkoPayState): IOkkoPayState => ({ ...state, loading: true });
const onRejected = (state: IOkkoPayState, action: Action<any>): IOkkoPayState => ({
    ...state,
    error: getRejectedRespData(action.payload),
    loading: false,
});
const onSetItem = (state: IOkkoPayState, action: Action<any>): IOkkoPayState => {
    const data = { ...action.payload };
    return {
        ...state,
        data,
        loading: false,
        list: action.type.startsWith(ACTION_TYPES.SAVE)
            ? state.list.map((el) => (el.id === data.id ? { ...el, name: data?.name } : el))
            : state.list,
    };
};

/* Reducer function end */

export default handleActions<IOkkoPayState, any>(
    {
        [ACTION_TYPES.UPDATE_STATE]: (state: IOkkoPayState, action: Action<any>): IOkkoPayState => ({
            ...state,
            ...action.payload,
        }),
        [ACTION_TYPES.SET_PAGING]: (state: IOkkoPayState, action: Action<IPaging>): IOkkoPayState => ({
            ...state,
            paging: { ...state.paging, ...parseToPaging(action.payload) },
        }),
        [ACTION_TYPES.RESET_PAGING]: (state: IOkkoPayState): IOkkoPayState => ({
            ...state,
            paging: { ...initPagingList },
        }),

        [PENDING(ACTION_TYPES.GET)]: onPending,
        [REJECTED(ACTION_TYPES.GET)]: onRejected,
        [FULFILLED(ACTION_TYPES.GET)]: (state: IOkkoPayState, action: Action<any>): IOkkoPayState => {
            const { page: list = [], ...paging } = action.payload;
            return { ...state, loading: false, list, paging: { ...state.paging, ...paging } };
        },

        [PENDING(ACTION_TYPES.GET_LIST)]: onPending,
        [REJECTED(ACTION_TYPES.GET_LIST)]: onRejected,
        [FULFILLED(ACTION_TYPES.GET_LIST)]: (state: IOkkoPayState, action: Action<any[]>): IOkkoPayState => ({
            ...state,
            loading: false,
            listShort: action.payload?.map((el) => Object.assign(el, { name: `${el.name} (${el.code})` })) || [],
        }),

        [PENDING(ACTION_TYPES.GET_BY_ID)]: onPending,
        [REJECTED(ACTION_TYPES.GET_BY_ID)]: onRejected,
        [FULFILLED(ACTION_TYPES.GET_BY_ID)]: onSetItem,

        [PENDING(ACTION_TYPES.DELETE)]: onPending,
        [REJECTED(ACTION_TYPES.DELETE)]: onRejected,
        [FULFILLED(ACTION_TYPES.DELETE)]: (state: IOkkoPayState, action: Action<any>): IOkkoPayState => ({
            ...state,
            loading: false,
            list: state.list.filter((el: IModel) => el.id !== action.payload),
            listShort: state.listShort.filter((el: IModel) => el.id !== action.payload),
        }),

        [PENDING(ACTION_TYPES.SAVE)]: onPending,
        [REJECTED(ACTION_TYPES.SAVE)]: onRejected,
        [FULFILLED(ACTION_TYPES.SAVE)]: onSetItem,

        /* WS events */
        /* WS events end */
    },
    initState,
);
